import { defineStyle, defineCssVars, type StyleFunctionProps } from '@chakra-ui/react';
import { Space, TextStyles, Radii, FontWeights, Shadows } from '../../types';

const vars = defineCssVars('badge', ['bg', 'color', 'shadow', 'iconColor']);

const baseStyle = defineStyle(props => {
  const { isRound } = props;
  return {
    px: isRound ? Space.XS : Space['2XS'],
    py: '0.5',
    textTransform: 'initial',
    apply: `textStyles.${TextStyles.P4}`,
    fontWeight: FontWeights.SEMIBOLD,
    display: 'inline-flex',
    alignItems: 'center',
    flexGrow: '0',
    w: 'fit-content',
    alignSelf: 'center',
    gap: Space['2XS'],
    borderRadius: isRound ? Radii.FULL : Radii.SM,
    bg: vars.bg.reference,
    bgColor: vars.bg.reference,
    color: vars.color.reference,
    ring: '1px',
    ringColor: vars.shadow.reference,
    boxShadow: Shadows['2XS'],
    '& > span:has(.svg-inline--fa)': {
      m: 0,
      color: vars.iconColor.reference,
      '&:first-child': {
        ms: isRound ? -1 : 0,
      },
      '&:last-child': {
        me: isRound ? -1 : 0,
      },
    },
  };
});

const variants = {
  subtle: (props: StyleFunctionProps) => {
    const { colorScheme: c } = props;
    return {
      [vars.bg.variable]: `colors.${c}.50`,
      [vars.color.variable]: `colors.${c}.600`,
      [vars.iconColor.variable]: `colors.${c}.500`,
      _dark: {
        [vars.bg.variable]: `colors.${c}.800`,
        [vars.color.variable]: `colors.${c}.200`,
        [vars.iconColor.variable]: `colors.${c}.3400`,
      },
    };
  },
  solid: (props: StyleFunctionProps) => {
    const { colorScheme: c } = props;
    return {
      [vars.bg.variable]: `colors.${c}.500`,
      [vars.color.variable]: `colors.white`,
      [vars.iconColor.variable]: `colors.${c}.100`,
      _dark: {
        [vars.bg.variable]: `colors.${c}.600`,
        [vars.color.variable]: `colors.${c}.50`,
        [vars.iconColor.variable]: `colors.${c}.100`,
      },
    };
  },
  outline: (props: StyleFunctionProps) => {
    const { colorScheme: c } = props;
    return {
      [vars.color.variable]: `colors.${c}.500`,
      [vars.bg.variable]: `colors.${c}.25`,
      [vars.shadow.variable]: `colors.${c}.200`,
      [vars.iconColor.variable]: `colors.${c}.400`,
      _dark: {
        [vars.color.variable]: `colors.${c}.300`,
        [vars.bg.variable]: `colors.${c}.975`,
        [vars.shadow.variable]: `colors.${c}.700`,
        [vars.iconColor.variable]: `colors.${c}.400`,
      },
    };
  },
};

export const Badge = {
  baseStyle,
  variants,
  defaultProps: {
    variant: 'subtle',
    colorScheme: 'gray',
  },
};
